import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Form, Modal, Badge, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import "./PerformanceMetricManagementPage.css";
import EmptyState from "../../components/common/EmptyState";
import { FaChartLine } from "react-icons/fa";

// Phase 5, §5/§22/§32. Metric definitions catalogue. System (seeded)
// metrics are shown with their handler support status — a metric with no
// backend calculation handler cannot be attached to a profile (enforced
// server-side; shown here for transparency).
const DOMAINS = ["TASK_ACCOUNTABILITY", "TASK_CONTRIBUTION", "TASK_TAT", "TASK_WORKLOAD", "FORM_SUBMISSION", "FORM_SCORING", "FORM_REVIEW", "UAT_EXECUTION", "TASK_APPROVAL"];
const initialForm = { code: "", name: "", description: "", source_domain: "TASK_ACCOUNTABILITY", calculation_type: "COUNT", direction: "INFORMATIONAL_ONLY", unit: "", target_value: "", is_percentage: false };

export default function PerformanceMetricManagementPage() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [domainFilter, setDomainFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const res = await api.get("/performance-metrics", { params: { source_domain: domainFilter || undefined, pageSize: 100 } });
      setMetrics(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch metrics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMetrics(); }, [domainFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async (m) => {
    try {
      await api.patch(`/performance-metrics/${m.id}/status`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Metric deactivated" : "Metric activated");
      fetchMetrics();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update metric status");
    }
  };

  const handleCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.warning("Code and name are required"); return; }
    setSaving(true);
    try {
      await api.post("/performance-metrics", { ...form, target_value: form.target_value || null });
      toast.success("Metric created (not yet linked to a handler unless code matches a supported one)");
      setShowModal(false);
      setForm(initialForm);
      fetchMetrics();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create metric");
    } finally {
      setSaving(false);
    }
  };

  const hasActiveFilters = !!(search || domainFilter);
  const clearFilters = () => { setSearch(""); setDomainFilter(""); };

  const visibleMetrics = useMemo(() => {
    if (!search) return metrics;
    const q = search.trim().toLowerCase();
    return metrics.filter((m) => m.code?.toLowerCase().includes(q) || m.name?.toLowerCase().includes(q));
  }, [metrics, search]);

  const stats = useMemo(() => ({
    total: metrics.length,
    active: metrics.filter((m) => m.is_active).length,
    supported: metrics.filter((m) => m.has_calculation_handler).length,
  }), [metrics]);

  return (
    <div className="unit-page perfmetric-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Performance</div>
          <h2>Performance Metrics</h2>
          <p>Metric catalogue — raw calculation source, direction and score-compatibility, transparent for every profile.</p>
        </div>
        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchMetrics}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>
          <Can moduleKey="performance_metrics" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={() => setShowModal(true)}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              New Metric
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="perfmetric-stat-icon perfmetric-stat-icon-primary">
            <i className="fa-solid fa-gauge" aria-hidden="true"></i>
          </div>
          <div className="perfmetric-stat-text">
            <span>Total Metrics</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfmetric-stat-icon perfmetric-stat-icon-success">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <div className="perfmetric-stat-text">
            <span>Active</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfmetric-stat-icon perfmetric-stat-icon-accent">
            <i className="fa-solid fa-plug" aria-hidden="true"></i>
          </div>
          <div className="perfmetric-stat-text">
            <span>Handler Supported</span>
            <strong>{stats.supported}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-filter-row">
          <InputGroup className="unit-search" style={{ width: 220 }}>
            <InputGroup.Text aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search code or name..."
              aria-label="Search code or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Form.Select style={{ maxWidth: 220 }} value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
            <option value="">All Domains</option>
            {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
          </Form.Select>
          {hasActiveFilters && <Button className="unit-clear-filters-btn" onClick={clearFilters}>Clear Filters</Button>}
        </div>
        <div className="unit-table-wrapper">
          {loading ? <div className="unit-loader"><Spinner animation="border" /></div> : (
            <Table responsive className="unit-table">
              <thead><tr><th>Code</th><th>Name</th><th>Domain</th><th>Direction</th><th>Handler</th><th>Status</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {visibleMetrics.length ? visibleMetrics.map((m) => (
                  <tr key={m.id}>
                    <td><code className="perfmetric-code">{m.code}</code></td>
                    <td><strong>{m.name}</strong></td>
                    <td><Badge bg="light" text="dark">{m.source_domain}</Badge></td>
                    <td>{m.direction === "INFORMATIONAL_ONLY" ? <Badge bg="secondary">Informational</Badge> : m.direction}</td>
                    <td>{m.has_calculation_handler ? <Badge bg="success">Supported</Badge> : <Badge bg="danger">Unsupported</Badge>}</td>
                    <td><Badge className={`unit-status${m.is_active ? "" : " unit-status-inactive"}`}>{m.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td>
                      <div className="unit-actions">
                        <Can moduleKey="performance_metrics" actionKey="edit">
                          <Button
                            size="sm"
                            className="perfmetric-toggle-btn"
                            variant={m.is_active ? "outline-secondary" : "outline-success"}
                            onClick={() => toggleActive(m)}
                            title={m.is_active ? "Deactivate" : "Activate"}
                            aria-label={`${m.is_active ? "Deactivate" : "Activate"} ${m.name || "metric"}`}
                          >
                            <i className={`fa-solid ${m.is_active ? "fa-toggle-off" : "fa-toggle-on"}`} aria-hidden="true"></i>
                            {m.is_active ? "Deactivate" : "Activate"}
                          </Button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7">
                      <EmptyState
                        icon={FaChartLine}
                        title="No metrics found"
                        message={hasActiveFilters ? "Try another search or clear filters." : "Create your first performance metric to get started."}
                        action={hasActiveFilters ? (
                          <Button className="unit-btn unit-btn-light" onClick={clearFilters}>
                            <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
                            Clear Filters
                          </Button>
                        ) : (
                          <Can moduleKey="performance_metrics" actionKey="create">
                            <Button className="unit-btn unit-btn-primary" onClick={() => setShowModal(true)}>
                              <i className="fa-solid fa-plus" aria-hidden="true"></i>
                              New Metric
                            </Button>
                          </Can>
                        )}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="unit-modal">
        <Modal.Header closeButton><Modal.Title>New Metric Definition</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Label>Code</Form.Label><Form.Control value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></Col>
            <Col md={6}><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
            <Col md={12}><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Col>
            <Col md={4}>
              <Form.Label>Source Domain</Form.Label>
              <Form.Select value={form.source_domain} onChange={(e) => setForm({ ...form, source_domain: e.target.value })}>
                {DOMAINS.map((d) => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Calculation Type</Form.Label>
              <Form.Select value={form.calculation_type} onChange={(e) => setForm({ ...form, calculation_type: e.target.value })}>
                {["COUNT", "SUM", "AVERAGE", "PERCENTAGE", "RATIO", "DURATION", "TARGET_ACHIEVEMENT"].map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Direction</Form.Label>
              <Form.Select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
                {["HIGHER_IS_BETTER", "LOWER_IS_BETTER", "INFORMATIONAL_ONLY"].map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}><Form.Label>Unit (optional)</Form.Label><Form.Control value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></Col>
            <Col md={4}><Form.Label>Target Value (optional)</Form.Label><Form.Control type="number" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} /></Col>
            <Col md={4} className="d-flex align-items-end">
              <Form.Check type="checkbox" label="Is Percentage" checked={form.is_percentage} onChange={(e) => setForm({ ...form, is_percentage: e.target.checked })} />
            </Col>
          </Row>
          <div className="text-muted small mt-2">Note: a metric only counts toward a scorecard total if its code matches a backend-supported calculation handler (see PHASE_5_METRIC_CATALOGUE.md).</div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Saving..." : "Create"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
